/**
 * Binds the unified map's view state to the browser URL.
 *
 * Every filter, ordering and viewport change the map makes goes through here
 * and lands in the query string, which is what makes a map link shareable and
 * back/forward navigation meaningful. Panning writes with `replace` so dragging
 * across a map does not bury the previous page under a hundred history entries;
 * a deliberate filter change pushes, because "back" should undo it.
 *
 * Each write is also remembered per customer, so the sidebar Map link can
 * return the user to the map they left rather than a reset one.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { parseMapSearch, serializeMapSearch } from '@sophocles/features/map/mapUrlState';
import { rememberMapSearch } from '@sophocles/features/map/slice';

// Panning/zooming only moves the viewport; it replaces rather than pushes.
const REPLACE_ONLY_KEYS = new Set(['bbox']);

function isViewportOnly(patch) {
  const keys = Object.keys(patch);
  return keys.length > 0 && keys.every(key => REPLACE_ONLY_KEYS.has(key));
}

/**
 * Read and write the map's URL-backed view state.
 *
 * @param {string|number} [customerId] - customer whose map search to remember; omit to skip remembering.
 * @returns {{ state: object, setState: (patch: object) => void }}
 *   `state` is the parsed map state (see `parseMapSearch`); `setState` merges a
 *   partial patch into it and writes the result back to the URL.
 */
export default function useMapUrlState(customerId) {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const search = searchParams.toString();
  const state = useMemo(() => parseMapSearch(search), [search]);

  // setState reads the current state at call time, so a handler captured by a
  // Leaflet listener never writes back over a newer filter selection.
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (customerId == null) return;
    dispatch(rememberMapSearch({ customerId, search }));
  }, [customerId, search, dispatch]);

  const setState = useCallback(
    patch => {
      const next = serializeMapSearch({ ...stateRef.current, ...patch });
      setSearchParams(next, { replace: isViewportOnly(patch) });
    },
    [setSearchParams],
  );

  return { state, setState };
}
